// PRD-0190 publish handoff status for an approved blog post: Ready to publish → Waiting for site
// agent → Claimed by <agent> → Published at <url> (or the failure reason). HelloConvo never publishes
// to the site itself; the brand's site agent does, via the hc CLI / MCP.
import { useState } from "react";
import type { Brief, ReviewApprovalItem } from "../../../../shared/types";
import type { PublishHandoff } from "../../../../shared/publish-handoff";
import { useMarkReadyToPublish, usePublishHandoff } from "../../hooks/usePublishHandoff";
import { LoadErrorState } from "../ui/LoadState";

interface Props {
  brief: Brief | ReviewApprovalItem;
  brandId: string;
  /** Approved (or already scheduled/published) and the draft has loaded. */
  canMarkReady: boolean;
}

function safeUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function when(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : "";
}

/**
 * A claim whose lease has passed is claimable again even if the server still reports `claimed`
 * (e.g. `hc publish list` before the sweep runs), so treat it as Ready.
 */
export function isClaimAvailable(handoff: PublishHandoff, now = Date.now()): boolean {
  if (handoff.status !== "claimed") return false;
  if (handoff.available) return true;
  const lease = handoff.lease_expires_at ? Date.parse(handoff.lease_expires_at) : Number.NaN;
  return Number.isFinite(lease) && lease <= now;
}

function statusCopy(handoff: PublishHandoff): { title: string; body: string; tone: "info" | "success" | "warning" } {
  switch (handoff.status) {
    case "ready":
      return {
        title: "Waiting for site agent",
        body: handoff.failure_reason
          ? `Released for another attempt: ${handoff.failure_reason}`
          : "Your site agent picks this up with hc publish list / claim, publishes it in the site repo and reports the live URL here.",
        tone: "info",
      };
    case "claimed":
      return isClaimAvailable(handoff)
        ? { title: "Waiting for site agent", body: `The claim by ${handoff.claimed_by ?? "an agent"} expired; any site agent can claim it again.`, tone: "info" }
        : { title: `Claimed by ${handoff.claimed_by ?? "a site agent"}`, body: `Publishing in the site repo. Lease until ${when(handoff.lease_expires_at)}.`, tone: "info" };
    case "verifying":
      return { title: "Checking the live page", body: `Reported at the URL below. ${handoff.verification.detail ?? ""} Next check ${when(handoff.verification.next_check_at)}.`.trim(), tone: "info" };
    case "published":
      return { title: "Published", body: `Live and verified ${when(handoff.verification.verified_at)}.`, tone: "success" };
    case "failed":
      return { title: "Publishing failed", body: handoff.failure_reason ?? "The site agent reported a failure.", tone: "warning" };
    case "withdrawn":
      return { title: "Withdrawn", body: handoff.failure_reason ?? "Pulled from publishing.", tone: "warning" };
    case "cancelled":
      return { title: "Ready cancelled", body: handoff.failure_reason ?? "The draft changed after approval.", tone: "warning" };
  }
}

const TONES = {
  info: "border-sky-200 bg-sky-50 text-sky-950",
  success: "border-emerald-200 bg-emerald-50 text-emerald-950",
  warning: "border-amber-200 bg-amber-50 text-amber-950",
} as const;

export function PublishHandoffPanel({ brief, brandId, canMarkReady }: Props) {
  const handoffQ = usePublishHandoff(brandId, brief.id, true);
  const markReady = useMarkReadyToPublish(brandId, brief.id, brief.campaign_id);
  const [error, setError] = useState<string | null>(null);
  const handoff = handoffQ.data ?? null;
  const canReReady = !handoff || ["failed", "withdrawn", "cancelled"].includes(handoff.status);
  // The server re-checks approval and the pinned revision; a just-approved brief may not have refreshed yet.
  const showReadyButton = canMarkReady && canReReady && brief.state !== "published";

  async function handleReady() {
    setError(null);
    try {
      await markReady.mutateAsync();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not mark ready to publish");
    }
  }

  if (handoffQ.isLoading) return <p className="text-xs text-slate-400" data-testid="publish-handoff-loading">Loading publish status…</p>;
  if (handoffQ.isError) {
    return (
      <LoadErrorState compact title="Publish status could not be loaded" description="Ready to publish is paused until it loads." onRetry={() => void handoffQ.refetch()} />
    );
  }

  const copy = handoff ? statusCopy(handoff) : null;
  const url = safeUrl(handoff?.published_url);
  return (
    <div className="space-y-2" data-testid="publish-handoff">
      {copy && (
        <div className={`rounded-md border p-3 text-sm ${TONES[copy.tone]}`}>
          <p className="font-semibold">{copy.title}</p>
          <p className="mt-1 text-xs">{copy.body}</p>
          {handoff?.published_url && (
            url ? (
              <a href={url} target="_blank" rel="noreferrer" className="mt-2 block break-all text-xs font-medium underline">
                {handoff.status === "published" ? "Published at " : "Reported URL: "}{handoff.published_url}
              </a>
            ) : (
              <p className="mt-2 break-all text-xs font-medium">{handoff.published_url}</p>
            )
          )}
          {handoff?.pr_url && safeUrl(handoff.pr_url) && (
            <a href={safeUrl(handoff.pr_url)!} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs underline">Site pull request</a>
          )}
        </div>
      )}
      {showReadyButton ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleReady}
            disabled={markReady.isPending}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {markReady.isPending ? "Marking ready…" : handoff ? "Mark ready again" : "Ready to publish"}
          </button>
          <p className="text-xs text-slate-500">Hands this exact approved version to your site agent. Editing the draft afterwards cancels it.</p>
        </div>
      ) : !handoff && !canMarkReady ? (
        <p className="text-xs text-slate-500">Approve this draft, then mark it Ready to publish for your site agent.</p>
      ) : null}
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
